import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

const getScrollParents = (node) => {
  const parents = [];
  let element = node?.parentElement;

  while (element) {
    const style = window.getComputedStyle(element);
    const overflow = `${style.overflow} ${style.overflowX} ${style.overflowY}`;
    if (/(auto|scroll|overlay)/.test(overflow)) {
      parents.push(element);
    }
    element = element.parentElement;
  }

  return parents;
};

const useFloatingMenu = ({
  isOpen,
  onClose,
  align = 'right',
  gap = 4,
  viewportPadding = 8,
}) => {
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  const updatePosition = useCallback(() => {
    const trigger = triggerRef.current;
    const menu = menuRef.current;
    if (!trigger || !menu) return;

    const rect = trigger.getBoundingClientRect();
    const menuHeight = menu.offsetHeight;
    const menuWidth = menu.offsetWidth;
    const spaceBelow = window.innerHeight - rect.bottom - viewportPadding;
    const spaceAbove = rect.top - viewportPadding;
    const openUp = spaceBelow < menuHeight + gap && spaceAbove > spaceBelow;

    let top = openUp ? rect.top - menuHeight - gap : rect.bottom + gap;
    let left = align === 'right' ? rect.right - menuWidth : rect.left;

    left = Math.max(
      viewportPadding,
      Math.min(left, window.innerWidth - menuWidth - viewportPadding)
    );
    top = Math.max(
      viewportPadding,
      Math.min(top, window.innerHeight - menuHeight - viewportPadding)
    );

    setPosition({ top, left });
  }, [align, gap, viewportPadding]);

  useLayoutEffect(() => {
    if (!isOpen) return;
    updatePosition();
  }, [isOpen, updatePosition]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleUpdate = () => updatePosition();
    const scrollParents = getScrollParents(triggerRef.current);

    window.addEventListener('scroll', handleUpdate, true);
    window.addEventListener('resize', handleUpdate);
    scrollParents.forEach((parent) => {
      parent.addEventListener('scroll', handleUpdate, { passive: true });
    });

    const handleClickOutside = (event) => {
      if (
        triggerRef.current?.contains(event.target)
        || menuRef.current?.contains(event.target)
      ) {
        return;
      }
      onClose();
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      window.removeEventListener('scroll', handleUpdate, true);
      window.removeEventListener('resize', handleUpdate);
      scrollParents.forEach((parent) => {
        parent.removeEventListener('scroll', handleUpdate);
      });
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose, updatePosition]);

  return { triggerRef, menuRef, position };
};

export default useFloatingMenu;
